import React, { useCallback, useLayoutEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import ErrorBoundary from "../Preview/ErrorBoundary";
import { FormProvider } from "../FormProvider";
import { useApp } from "../../context/AppState";
import { POPUP_SECTIONS } from "../../constant/constant";
import DividerBlock from "../Preview/Draggable/Block/DividerBlock";
import {
  CART_DRAWER_DEFAULT_LINE_IMAGE,
  measureThemeCartLineItemImageSize,
  pickCartDrawerStepVisualCss,
  sortSectionsForCartDrawerFlow,
} from "../../utils/cartDrawerLayout";
import {
  getCartDrawerFlowCss,
  getCartDrawerImageColumnSize,
} from "../../utils/sectionBlockCss";
import {
  getStepSectionsForPopup,
  stripShellDimensionsFromCustomCSS,
} from "../../utils/popup-step-sections";
import {
  getCartDrawerBrandAttributionPositionStyle,
  shouldShowPopupWatermark,
} from "../../utils/popup-watermark";
import { isSectionVisible } from "../../../shared/section-visibility.js";
import { ensureExtensionBrandAttributionSections } from "../../layouts/checkout-extension/shared/ensure-extension-brand-attribution";
import AiBrandAttribution from "../../layouts/ai-recommendation-upsell/components/AiBrandAttribution";
import TextBlock from "../Preview/Draggable/Block/TextBlock";
import ButtonBlock from "../Preview/Draggable/Block/ButtonBlock";
import ImageSliderBlock from "../Preview/Draggable/Block/ImageSliderBlock";
import ImageBlock from "../Preview/Draggable/Block/ImageBlock";
import QuantityPickerBlock from "../Preview/Draggable/Block/QuantityPickerBlock";
import VariantPickerBlock from "../Preview/Draggable/Block/VariantPickerBlock";
import { applyCompactExtensionWidgetSubsectionDefaults } from "../../../shared/extension-widget-display-defaults.js";
import PriceBlock from "../Preview/Draggable/Block/PriceBlock";
import SubscriptionPickerBlock from "../Preview/Draggable/Block/SubscriptionPickerBlock";
import CustomHtmlBlock from "../Preview/Draggable/Block/CustomHtmlBlock";
import ChoiceBlock from "../Preview/Draggable/Block/ChoiceBlock";
import SubscriptionBlock from "../Preview/Draggable/Block/SubscriptionBlock";
import RatingBlock from "../Preview/Draggable/Block/RatingBlock";
import CartDrawerBlockShell from "./CartDrawerBlockShell";
import { useCartDrawerProduct } from "./useCartDrawerProduct";

const IMAGE_TYPES = [
  POPUP_SECTIONS.COVER_IMAGE,
  POPUP_SECTIONS.PRODUCT_IMAGE,
];
const TITLE_TYPES = [
  POPUP_SECTIONS.TITLE,
  POPUP_SECTIONS.PRODUCT_TITLE,
  POPUP_SECTIONS.DESCRIPTION,
];
const PRICE_TYPES = [POPUP_SECTIONS.PRICE];
const VARIANT_TYPES = [
  POPUP_SECTIONS.VARIANT_PICKER,
  POPUP_SECTIONS.SUBSCRIPTION_PICKER,
];
const QUANTITY_TYPES = [
  POPUP_SECTIONS.QUANTITY_PICKER,
  POPUP_SECTIONS.QUANTITY,
];
const BUTTON_TYPES = [POPUP_SECTIONS.BUTTON];

function normalizePopupSchema(popupData) {
  if (!Array.isArray(popupData?.schema)) {
    return [];
  }

  return popupData.schema
    .map((item) => {
      if (!item) return null;
      const sections =
        Array.isArray(item.sections) && item.sections.length > 0
          ? item.sections
          : Array.isArray(item.section)
            ? item.section
            : [];
      return {
        ...item,
        sections,
        popupSettings: item.popupSettings || {},
      };
    })
    .filter(Boolean);
}

const defaultCartDrawerState = {
  activeStep: 0,
  openPopup: true,
  triggerType: "cart_drawer",
  triggeredAt: Date.now(),
  closedAt: null,
};

const noop = () => {};

const TEXT_FLOW_TYPES = new Set([
  POPUP_SECTIONS.TEXT,
  POPUP_SECTIONS.TITLE,
  POPUP_SECTIONS.SUBTITLE,
  POPUP_SECTIONS.DESCRIPTION,
  POPUP_SECTIONS.PARAGRAPH,
  POPUP_SECTIONS.PRODUCT_TITLE,
]);

function CartDrawerFlowSection({ section, children, className, compact = false }) {
  const { isMobile } = useApp();
  const blockCSS = getCartDrawerFlowCss(section, isMobile);

  return (
    <CartDrawerBlockShell
      section={section}
      className={
        className
          ? `weupsell-cart-drawer-flow-section ${className}`
          : "weupsell-cart-drawer-flow-section"
      }
      style={{
        ...blockCSS,
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        height: compact ? "auto" : blockCSS.height,
        minHeight: compact ? 0 : blockCSS.minHeight,
        marginBottom: compact ? "6px" : "8px",
        boxSizing: "border-box",
      }}
    >
      {children}
    </CartDrawerBlockShell>
  );
}

function pickSubsections(subsections, types, usedIndexes) {
  const list = [];
  subsections.forEach((section, index) => {
    if (types.includes(section?.type) && !usedIndexes.has(index)) {
      usedIndexes.add(index);
      list.push({ section, index });
    }
  });
  return list;
}

function CartDrawerProductGrid({
  productSection,
  productData,
  productIndex,
  popupData,
  popupState,
  setPopupState,
}) {
  const { isMobile } = useApp();
  const [themeLineImage, setThemeLineImage] = useState(null);

  useLayoutEffect(() => {
    const measure = () => {
      setThemeLineImage(
        measureThemeCartLineItemImageSize() || CART_DRAWER_DEFAULT_LINE_IMAGE
      );
    };
    measure();
    const observer = new MutationObserver(measure);
    const drawer =
      document.querySelector("cart-drawer") ||
      document.getElementById("CartDrawer");
    if (drawer) {
      observer.observe(drawer, {
        childList: true,
        subtree: true,
        attributes: true,
      });
    }
    return () => observer.disconnect();
  }, []);

  const blockCSS = useMemo(
    () => getCartDrawerFlowCss(productSection, isMobile),
    [productSection, isMobile]
  );

  const subsections = Array.isArray(productSection?.sections)
    ? productSection.sections
    : [];

  const renderSubsection = useCallback(
    (subsection, subIndex) => {
      const resolvedSubsection = applyCompactExtensionWidgetSubsectionDefaults(
        subsection
      );
      switch (resolvedSubsection?.type) {
        case POPUP_SECTIONS.TITLE:
        case POPUP_SECTIONS.PRODUCT_TITLE:
        case POPUP_SECTIONS.DESCRIPTION:
          return (
            <TextBlock section={resolvedSubsection} parentData={productData} />
          );
        case POPUP_SECTIONS.COVER_IMAGE:
          return (
            <ImageSliderBlock
              section={resolvedSubsection}
              productIndex={productIndex}
              productData={productData}
            />
          );
        case POPUP_SECTIONS.PRODUCT_IMAGE:
          return (
            <ImageBlock
              section={resolvedSubsection}
              setPopupState={setPopupState}
              handleClose={noop}
              popupData={popupData}
              popupState={popupState}
              parentData={productData}
            />
          );
        case POPUP_SECTIONS.PRICE:
          return (
            <PriceBlock
              productData={productData}
              section={resolvedSubsection}
              index={subIndex}
              productIndex={productIndex}
              popupData={popupData}
            />
          );
        case POPUP_SECTIONS.VARIANT_PICKER:
          return (
            <VariantPickerBlock
              popupData={popupData}
              section={resolvedSubsection}
              index={subIndex}
              productIndex={productIndex}
              productData={productData}
            />
          );
        case POPUP_SECTIONS.SUBSCRIPTION_PICKER:
          return (
            <SubscriptionPickerBlock
              section={resolvedSubsection}
              index={subIndex}
              productIndex={productIndex}
              productData={productData}
            />
          );
        case POPUP_SECTIONS.QUANTITY_PICKER:
        case POPUP_SECTIONS.QUANTITY:
          return (
            <QuantityPickerBlock
              section={resolvedSubsection}
              index={subIndex}
              productIndex={productIndex}
            />
          );
        case POPUP_SECTIONS.BUTTON:
          return (
            <ButtonBlock
              popupData={popupData}
              popupState={popupState}
              setPopupState={setPopupState}
              section={resolvedSubsection}
              index={subIndex}
              productIndex={productIndex}
              productData={productData}
              handleClose={noop}
            />
          );
        case POPUP_SECTIONS.SUBSCRIPTION:
          return (
            <SubscriptionBlock
              section={subsection}
              productIndex={productIndex}
              productData={productData}
            />
          );
        case POPUP_SECTIONS.CHOICE:
          return <ChoiceBlock section={subsection} />;
        case POPUP_SECTIONS.CUSTOM_HTML:
          return (
            <CustomHtmlBlock section={subsection} parentData={productData} />
          );
        case POPUP_SECTIONS.RATING:
          return <RatingBlock section={subsection} />;
        default:
          return null;
      }
    },
    [popupData, popupState, setPopupState, productData, productIndex]
  );

  const renderSlot = (slot, layoutOverrides) => {
    const content = renderSubsection(slot.section, slot.index);
    if (!content) return null;
    const slotKey =
      slot.index === "cart-drawer-auto-variant"
        ? "cart-drawer-auto-variant"
        : slot.index;
    return (
      <CartDrawerBlockShell
        key={slotKey}
        section={
          slot.index === "cart-drawer-auto-variant" ? undefined : slot.section
        }
        className="weupsell-cart-drawer-block-slot"
        style={layoutOverrides}
      >
        {content}
      </CartDrawerBlockShell>
    );
  };

  const usedIndexes = new Set();
  const imageSlots = pickSubsections(subsections, IMAGE_TYPES, usedIndexes);
  const titleSlots = pickSubsections(subsections, TITLE_TYPES, usedIndexes);
  const priceSlots = pickSubsections(subsections, PRICE_TYPES, usedIndexes);
  const variantSlots = pickSubsections(subsections, VARIANT_TYPES, usedIndexes);
  const quantitySlots = pickSubsections(
    subsections,
    QUANTITY_TYPES,
    usedIndexes
  );
  const buttonSlots = pickSubsections(subsections, BUTTON_TYPES, usedIndexes);

  const otherSlots = subsections
    .map((section, index) => ({ section, index }))
    .filter(({ index }) => !usedIndexes.has(index));

  const { width: imageColWidth, height: imageColHeight } =
    getCartDrawerImageColumnSize(imageSlots[0]?.section, isMobile, {
      themeLineImage,
      fallback: CART_DRAWER_DEFAULT_LINE_IMAGE.width,
    });

  const hasQuantityPicker = quantitySlots.length > 0;
  const hasAddButton = buttonSlots.length > 0;
  const dualActionSlot = hasQuantityPicker && hasAddButton;
  const singleActionSlot =
    (hasQuantityPicker && !hasAddButton) || (!hasQuantityPicker && hasAddButton);

  const fullWidthSlotStyle = { width: "100%", maxWidth: "100%" };

  const variantCount = productData?.variants?.length ?? 0;
  const hasMultipleVariants = variantCount > 1;
  const effectiveVariantSlots =
    variantSlots.length > 0
      ? variantSlots
      : hasMultipleVariants
        ? [
            {
              section: {
                type: POPUP_SECTIONS.VARIANT_PICKER,
                setting: {},
              },
              index: "cart-drawer-auto-variant",
            },
          ]
        : [];

  const actionsRowClassName = [
    "weupsell-cart-drawer-actions-row",
    dualActionSlot && "weupsell-cart-drawer-actions-row--dual-slot",
    singleActionSlot && "weupsell-cart-drawer-actions-row--single-slot",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className="weupsell-cart-drawer-product-grid"
      style={{
        ...blockCSS,
        position: "relative",
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        margin: 0,
        boxSizing: "border-box",
        overflow: "visible",
      }}
    >
      <div
        className="weupsell-cart-drawer-product-row"
        style={{
          display: "grid",
          gridTemplateColumns: `${imageColWidth}px minmax(0, 1fr)`,
          gap: "10px",
          alignItems: "start",
          width: "100%",
          minWidth: 0,
        }}
      >
        <div
          className="weupsell-cart-drawer-image-cell"
          style={{
            width: imageColWidth,
            height: imageColHeight,
            minWidth: imageColWidth,
            minHeight: imageColHeight,
            overflow: "hidden",
            flexShrink: 0,
          }}
        >
          {imageSlots.map((slot) =>
            renderSlot(slot, { width: "100%", height: "100%" })
          )}
        </div>

        <div className="weupsell-cart-drawer-details-col" style={{ minWidth: 0, width: "100%" }}>
          {(titleSlots.length > 0 || priceSlots.length > 0) && (
            <div className="weupsell-cart-drawer-title-price-row">
              {titleSlots.length > 0 ? (
                <div
                  className="weupsell-cart-drawer-title-cell"
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                  }}
                >
                  {titleSlots.map((slot) =>
                    renderSlot(slot, fullWidthSlotStyle)
                  )}
                </div>
              ) : null}
              {priceSlots.length > 0 ? (
                <div className="weupsell-cart-drawer-price-cell">
                  {priceSlots.map((slot) =>
                    renderSlot(slot, fullWidthSlotStyle)
                  )}
                </div>
              ) : null}
            </div>
          )}

          {hasMultipleVariants && effectiveVariantSlots.length > 0 ? (
            <div className="weupsell-cart-drawer-variant-cell">
              {effectiveVariantSlots.map((slot) =>
                renderSlot(slot, {
                  marginTop: "10px",
                  ...fullWidthSlotStyle,
                })
              )}
            </div>
          ) : null}

          {(hasQuantityPicker || hasAddButton) && (
            <div className={actionsRowClassName}>
              {hasQuantityPicker ? (
                <div
                  className={
                    singleActionSlot
                      ? "weupsell-cart-drawer-qty-cell weupsell-cart-drawer-qty-cell--full"
                      : "weupsell-cart-drawer-qty-cell"
                  }
                >
                  {quantitySlots.map((slot) =>
                    renderSlot(
                      slot,
                      singleActionSlot || dualActionSlot
                        ? fullWidthSlotStyle
                        : undefined
                    )
                  )}
                </div>
              ) : null}
              {hasAddButton ? (
                <div
                  className={
                    singleActionSlot
                      ? "weupsell-cart-drawer-button-cell weupsell-cart-drawer-button-cell--full"
                      : "weupsell-cart-drawer-button-cell"
                  }
                >
                  {buttonSlots.map((slot) =>
                    renderSlot(slot, fullWidthSlotStyle)
                  )}
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {otherSlots.map((slot) => renderSlot(slot, { marginTop: "10px" }))}
    </div>
  );
}

function CartDrawerStepSection({
  section,
  sectionIndex,
  productSection,
  product,
  isLoading,
  popupData,
  popupState,
  setPopupState,
}) {
  if (!section) return null;

  if (section.type === POPUP_SECTIONS.PRODUCT) {
    if (isLoading) {
      return (
        <div
          key={`cart-step-${sectionIndex}`}
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "16px 0",
            width: "100%",
          }}
        >
          <div
            style={{
              width: "32px",
              height: "32px",
              border: "3px solid #f3f3f3",
              borderTop: "3px solid #3498db",
              borderRadius: "50%",
              animation: "weupsell-cart-drawer-spin 1s linear infinite",
            }}
          />
        </div>
      );
    }
    if (!product) return null;
    return (
      <CartDrawerProductGrid
        key={`cart-step-${sectionIndex}`}
        productSection={productSection || section}
        productData={product}
        productIndex={0}
        popupData={popupData}
        popupState={popupState}
        setPopupState={setPopupState}
      />
    );
  }

  if (section.type === POPUP_SECTIONS.DIVIDER) {
    return (
      <CartDrawerFlowSection
        key={`cart-step-${sectionIndex}`}
        section={section}
        className="weupsell-cart-drawer-flow-divider"
        compact
      >
        <DividerBlock section={section} fullWidth />
      </CartDrawerFlowSection>
    );
  }

  if (TEXT_FLOW_TYPES.has(section.type)) {
    return (
      <CartDrawerFlowSection
        key={`cart-step-${sectionIndex}`}
        section={section}
        className="weupsell-cart-drawer-flow-text"
      >
        <TextBlock section={section} />
      </CartDrawerFlowSection>
    );
  }

  if (section.type === POPUP_SECTIONS.CUSTOM_HTML) {
    return (
      <CartDrawerFlowSection
        key={`cart-step-${sectionIndex}`}
        section={section}
      >
        <CustomHtmlBlock section={section} />
      </CartDrawerFlowSection>
    );
  }

  if (section.type === POPUP_SECTIONS.BUTTON) {
    return (
      <CartDrawerFlowSection
        key={`cart-step-${sectionIndex}`}
        section={section}
      >
        <ButtonBlock
          popupData={popupData}
          popupState={popupState}
          setPopupState={setPopupState}
          section={section}
          index={sectionIndex}
          handleClose={noop}
        />
      </CartDrawerFlowSection>
    );
  }

  if (section.type === POPUP_SECTIONS.BRAND_ATTRIBUTION) {
    return null;
  }

  return null;
}

const RenderCartDrawerWidget = ({
  popupData,
  popupState: popupStateProp,
  cartDrawerWidgetBounds,
}) => {
  const { isMobile, appState } = useApp();

  const [popupState, setPopupState] = useState(() => ({
    ...defaultCartDrawerState,
    ...(popupStateProp || {}),
    openPopup: true,
  }));

  const popupDataWithValidatedSchema = useMemo(
    () => ({
      ...popupData,
      schema: normalizePopupSchema(popupData),
    }),
    [popupData]
  );

  const methods = useForm({ defaultValues: {}, mode: "onSubmit" });
  const { handleSubmit } = methods;

  const activeStep =
    typeof popupState?.activeStep === "number" ? popupState.activeStep : 0;

  const stepSections = useMemo(() => {
    const sections = ensureExtensionBrandAttributionSections(
      getStepSectionsForPopup(popupDataWithValidatedSchema, activeStep)
    );
    return sortSectionsForCartDrawerFlow(sections, isMobile);
  }, [popupDataWithValidatedSchema, activeStep, isMobile]);

  const brandAttributionSection = useMemo(
    () =>
      stepSections.find(
        (section) => section?.type === POPUP_SECTIONS.BRAND_ATTRIBUTION
      ) || null,
    [stepSections]
  );

  const productSection = useMemo(
    () => stepSections.find((s) => s?.type === POPUP_SECTIONS.PRODUCT) || null,
    [stepSections]
  );

  const hasRenderableContent = useMemo(() => {
    if (stepSections.length === 0) return false;
    if (productSection) return true;
    return stepSections.some(
      (s) =>
        s?.type === POPUP_SECTIONS.DIVIDER || TEXT_FLOW_TYPES.has(s?.type)
    );
  }, [stepSections, productSection]);

  const { product, isLoading } = useCartDrawerProduct({
    popupData: popupDataWithValidatedSchema,
    popupState,
    productSection,
    activeStep,
  });

  const popupSettings =
    popupDataWithValidatedSchema?.schema?.[activeStep]?.popupSettings || {};

  const padding =
    popupSettings?.dynamicalOptions?.window?.padding?.[
      isMobile ? "mobile" : "desktop"
    ] ?? 0;

  const stepViewCss = useMemo(() => {
    const raw =
      popupSettings?.customCSS?.[isMobile ? "mobile" : "desktop"] ?? {};
    return pickCartDrawerStepVisualCss(stripShellDimensionsFromCustomCSS(raw));
  }, [popupSettings, isMobile]);

  const showBrandAttribution = useMemo(
    () =>
      Boolean(brandAttributionSection) &&
      isSectionVisible(brandAttributionSection) &&
      shouldShowPopupWatermark(
        popupDataWithValidatedSchema,
        activeStep,
        appState?.watermark,
        appState?.is_preview_mode,
        appState?.shop
      ),
    [
      brandAttributionSection,
      popupDataWithValidatedSchema,
      activeStep,
      appState?.watermark,
      appState?.is_preview_mode,
      appState?.shop,
    ]
  );

  if (!popupState?.openPopup || !hasRenderableContent) {
    return null;
  }

  return (
    <ErrorBoundary>
      <FormProvider
        popupData={popupDataWithValidatedSchema}
        popupState={popupState}
        methods={methods}
        onSubmit={handleSubmit(() => {})}
        isCartDrawerWidget
        cartDrawerWidgetBounds={cartDrawerWidgetBounds}
      >
        <div
          className="weupsell-cart-drawer-widget-root"
          style={{
            ...stepViewCss,
            position: "relative",
            display: "block",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
            pointerEvents: "auto",
            overflow: "visible",
            padding: padding ? `${padding}px` : 0,
            margin: 0,
          }}
        >
          <div className="weupsell-cart-drawer-step-flow">
            {stepSections.map((section, sectionIndex) => (
              <CartDrawerStepSection
                key={`${section?.type}-${sectionIndex}`}
                section={section}
                sectionIndex={sectionIndex}
                productSection={productSection}
                product={product}
                isLoading={isLoading && section.type === POPUP_SECTIONS.PRODUCT}
                popupData={popupDataWithValidatedSchema}
                popupState={popupState}
                setPopupState={setPopupState}
              />
            ))}
          </div>

          {showBrandAttribution ? (
            <div
              className="weupsell-cart-drawer-brand-attribution"
              data-weupsell-brand-attribution="cart-drawer"
              style={getCartDrawerBrandAttributionPositionStyle()}
            >
              <AiBrandAttribution section={brandAttributionSection} />
            </div>
          ) : null}
        </div>
      </FormProvider>
    </ErrorBoundary>
  );
};

export default RenderCartDrawerWidget;
